import React from "react";
import { Input } from "@hopper/legacy_input";
import { Select } from "@hopper/select";
import { Textarea } from "@hopper/textarea";
import { FormField } from "@hopper/form_field";
import { Switch } from "@hopper/switch";
import { Checkbox } from "@hopper/checkbox";
import { Field, getIn, connect, FormikProps, FormikContextType, FieldProps, useFormikContext } from "formik";
import { Button, ButtonProps } from "@hopper/button";
import each from "lodash/each";
import map from "lodash/map";
import filter from "lodash/filter";
import { FormInlineError } from "@hopper/form_inline_error";
import { format } from "date-fns";
import classnames from "classnames";
import { Maybe } from "graphql/jsutils/Maybe";

import ControlledAutocomplete, { ControlledAutocompleteProps } from "./controlled_autocomplete";
import ControlledMultiselect from "./controlled_multiselect";
import TooltipTrigger from "components/shared/tooltip_trigger";
import { ValueOf } from "types/utility";

export type Option = {
  readonly label: string;
  readonly value: string;
};

export type FieldInterface<T = string, P = HTMLInputElement> = {
  label?: string | React.ReactNode;
  name: string;
  component?: React.ReactNode;
  value?: T;
  isOptional?: boolean;
  disabled?: boolean;
  type?: string;
  isLoading?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelect?: (opt: any) => void;
  onOutsideClick?: () => void;
  onLabel?: string | null;
  offLabel?: string | null;
} & Omit<React.HTMLAttributes<P>, "onSelect">;

export const errorMessage = (name: string, formik: FormikProps<unknown>) => {
  let validationError = getIn(formik.errors, name);

  if (validationError) {
    if (Array.isArray(validationError)) {
      validationError = validationError.join(", ");
    }
    const touched = getIn(formik.touched, name);

    if (touched) {
      return validationError;
    }
  }

  const statusError =
    formik.status && formik.status.errors && (getIn(formik.status.errors, name) || formik.status.errors[name]);

  if (statusError) {
    return statusError.toString();
  }
};

const DatePicker = React.lazy(() => import("react-date-picker"));

type ProxiedField<T = HTMLInputElement> = React.HTMLAttributes<T> & FieldProps<string>;

const InputField = ({ field, meta, form, ...props }: ProxiedField) => <Input {...field} {...props} />;

const TextareaField = ({ field, meta, form, ...props }: ProxiedField<HTMLTextAreaElement>) => (
  <Textarea {...field} {...props} />
);

type ProxiedFieldWithOptions<T> = ProxiedField<T> & { options: Option[] };

const SelectField = ({ field, meta, form, ...props }: ProxiedFieldWithOptions<HTMLSelectElement>) => (
  <Select {...field} {...props} />
);

const AutocompleteFormikField = ({
  field,
  meta,
  form,
  ...props
}: ControlledAutocompleteProps & ProxiedFieldWithOptions<HTMLSelectElement>) => (
  <ControlledAutocomplete {...field} {...props} />
);

type SwitchFieldProps = Parameters<typeof Switch> & {
  field: FieldInterface<string, HTMLInputElement>;
  form: FormikContextType<unknown>;
  onLabel?: string;
  offLabel?: string;
};

const SwitchField = ({ field, form, onLabel = "yes", offLabel = "no", ...otherProps }: SwitchFieldProps) => {
  const rawValue = getIn(form.values, field.name);
  const isOn = rawValue && rawValue.toString() === "true";

  return (
    <Switch
      isOn={isOn}
      {...field}
      {...otherProps}
      onChange={() => {
        form.setFieldValue(field.name, !isOn);
      }}
      onLabel={onLabel}
      offLabel={offLabel}
    />
  );
};

type RadioFieldProps = FieldInterface<string | number | readonly string[] | undefined, HTMLInputElement>;

const RadioField = ({ name, label, value, ...otherProps }: RadioFieldProps) => (
  <Field name={name}>
    {({ field }: FieldProps<string | number | readonly string[] | undefined>) => {
      const { value: currentValue, ...fieldProps } = field;

      const isChecked = currentValue === value;

      return (
        <>
          <label data-test-role="radio">
            <input type="radio" value={value} checked={isChecked} {...otherProps} {...fieldProps} />
            <span className="ml-sm align-middle">{label}</span>
          </label>
        </>
      );
    }}
  </Field>
);

const CheckboxField = ({ name, label, ...otherProps }: FieldInterface<string, HTMLInputElement>) => (
  <Field name={name}>
    {({ form, field }: FieldProps<unknown>) => {
      const isChecked = getIn(form.values, field.name);
      const error = errorMessage(field.name, form);

      return (
        <>
          <Checkbox
            isChecked={isChecked}
            onChange={async () => {
              await form.setFieldValue(field.name, !isChecked);
              form.setFieldTouched(field.name);
            }}
            label={label as string}
            {...otherProps}
          />
          {error && <FormInlineError>{error}</FormInlineError>}
        </>
      );
    }}
  </Field>
);

const DateField = ({ field, form }: { field: FieldInterface<string>; form: FormikProps<unknown> }) => {
  const error = errorMessage(field.name, form);
  const datePickerClassNames = classnames([
    "form-date-field px-md bg-white inline-flex w-full h-5xl",
    "border border-solid rounded-base focus:outline-none",
    "font-normal text-tw-secondary-800 text-base leading-150",
    { "border-1 border-tw-neutral-200 hover:border-tw-neutral-500": !error },
    { "border-2 border-tw-danger-500": !!error },
  ]);

  return (
    <DatePicker
      value={field.value ? new Date(field.value) : null}
      showLeadingZeros={true}
      clearIcon={null}
      calendarIcon={null}
      className={datePickerClassNames}
      calendarClassName="text-xs leading-sm"
      format="yyyy-MM-dd"
      minDate={new Date(1900, 1, 1)}
      maxDate={new Date()}
      defaultView="century"
      onChange={(value: number | Date) => {
        if (value) {
          form.setFieldValue(field.name, format(value, "yyyy/MM/dd"));
        } else {
          form.setFieldValue(field.name, null);
        }
      }}
    />
  );
};

type FormikFieldProps = FieldInterface<unknown, HTMLElement> & {
  options?: Option[] | Maybe<Option[]>;
  validate?: (value: string) => string | undefined;
};

const FormikField = ({ label, name, component, isOptional = true, hidden, ...otherProps }: FormikFieldProps) => {
  const formik = useFormikContext();
  const error = errorMessage(name, formik);

  return (
    <div key={name} className="mb-md">
      <FormField label={!hidden && label} errorMessage={error} isOptional={isOptional}>
        <Field name={name} component={component || InputField} hidden={hidden} invalid={!!error} {...otherProps} />
      </FormField>
    </div>
  );
};

const Fieldset = ({
  children,
  "data-testid": testId,
  separator = true,
}: {
  children: React.ReactNode;
  "data-testid"?: string;
  separator?: boolean;
}) => (
  <div data-testid={testId}>
    {children}
    {separator ? <hr className="border-muted my-4xl" /> : null}
  </div>
);

const MultiSelectField = ({
  field,
  form,
  options,
  disabled,
}: {
  field: { name: string; value: unknown[] };
  form: FormikProps<unknown>;
  options: Option[];
  disabled: boolean;
}) => {
  const items = map(options, ({ value, label }) => ({
    value,
    label,
    isSelected: field.value && field.value.includes(value),
  }));

  // INFO: By default, when a label is clicked, it focuses on the corresponding input element.
  // However, in this case, selecting an option in the list also triggers the focus,
  // causing the list to scroll to the top.
  // To work around this issue, we use a <span> wrapper to prevent the unintended scrolling behavior.
  // Jira: https://gatteam.atlassian.net/browse/TEAMX-3381
  return (
    <span onClick={e => e.preventDefault()}>
      <ControlledMultiselect
        items={items}
        onChange={(newItems: Option[]) => form.setFieldValue(field.name, map(filter(newItems, "isSelected"), "value"))}
        disabled={disabled}
      />
    </span>
  );
};

const LabelWithTooltip = ({ labelText, infoText }: { labelText: string | undefined; infoText?: string }) => {
  const tooltip = <div style={{ maxWidth: "180px" }}>{infoText}</div>;

  return (
    <>
      <span className="mr-xs">{labelText}</span>{" "}
      <TooltipTrigger tooltip={tooltip} placement="right">
        <span className="glyphicon glyphicon-info-sign" />
      </TooltipTrigger>
    </>
  );
};

// INFO: Not used at this moment - candidate to remove
const RadioInputGroup = connect(
  ({ name, options, formik }: { name: string; options: Option[]; formik: FormikContextType<unknown> }) => {
    const error = errorMessage(name, formik);

    // Inline RadioField style needed to override bootstrap defaults
    return (
      <>
        {options.map(({ label, value }) => (
          <span className="mr-sm">
            <RadioField style={{ marginRight: "0.25rem" }} key={value} name={name} label={label} value={value} />
          </span>
        ))}
        {error && <FormInlineError>{error}</FormInlineError>}
      </>
    );
  },
);

const RadioInputGroupField = ({
  field: { name },
  options,
  isVertical,
}: {
  field: { name: string };
  options: Option[];
  isVertical: boolean;
}) => {
  const wrapperClassName = isVertical ? "block" : "mr-sm";

  return (
    <>
      {options.map(({ label, value }) => (
        <span className={wrapperClassName} key={value}>
          <RadioField style={{ marginRight: "0.25rem" }} key={value} name={name} label={label} value={value} />
        </span>
      ))}
    </>
  );
};

const FullWidthField = ({ children }: { children: React.ReactNode }) => (
  <div className="full-width-field w-full mb-2xl">{children}</div>
);

const FullWidthFormikField = (props: FormikFieldProps) => (
  <FullWidthField>
    <FormikField {...props} />
  </FullWidthField>
);

const BaseError = () => (
  <Field>
    {({ form }: FieldProps) => {
      let errors = form.errors.base;

      if (Array.isArray(errors)) {
        errors = errors.join(", ");
      }

      if (errors) {
        return (
          <div className="my-md">
            <FormInlineError>{errors}</FormInlineError>
          </div>
        );
      } else {
        return "";
      }
    }}
  </Field>
);

type SubmitButtonProps = Omit<ButtonProps, "children"> & { disabled?: boolean };

const SubmitButton = ({ label = "Submit", disabled = false, ...otherProps }: SubmitButtonProps) => {
  const { isSubmitting } = useFormikContext();

  return (
    <Button type="submit" disabled={isSubmitting || disabled} isLoading={isSubmitting} {...otherProps}>
      {label}
    </Button>
  );
};
const withEmptyOption = (options: Readonly<Option[]>, placeholder = "", params = {}) => [
  { value: "", label: placeholder, disabled: true, ...params },
  ...options,
];

const stringifyOptions = (options: Option[]) =>
  options.map(({ value, label, ...otherOptions }) => ({
    value: value.toString(),
    label: label.toString(),
    ...otherOptions,
  }));

const replaceNullsWithEmptyStrings = <T extends Record<keyof T, ValueOf<T> | "">>(object: T, keys: (keyof T)[]) => {
  each(keys, key => {
    if (!object[key]) {
      // @ts-ignore
      object[key] = "";
    }
  });

  return object;
};

const ErrorMessage = connect(({ name, formik }: { name: string; formik: FormikContextType<unknown> }) => {
  const error = errorMessage(name, formik);

  return error ? <FormInlineError>{error}</FormInlineError> : null;
});

export {
  BaseError,
  CheckboxField,
  AutocompleteFormikField,
  InputField,
  SelectField,
  TextareaField,
  LabelWithTooltip,
  FormikField,
  SwitchField,
  RadioField,
  RadioInputGroup,
  DateField,
  FullWidthFormikField,
  Fieldset,
  FullWidthField,
  MultiSelectField,
  SubmitButton,
  ErrorMessage,
  withEmptyOption,
  stringifyOptions,
  replaceNullsWithEmptyStrings,
  RadioInputGroupField,
};
